
'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Menu, User, LogOut, Shield, X, ChevronDown } from 'lucide-react';
import Logo from '@/components/logo';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import React from 'react';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

type NavItem = {
  label: string;
  href?: string;
  children?: { href: string; label: string }[];
  hidden?: boolean;
};

const navItems: NavItem[] = [
  { href: '/', label: 'Home' },
  {
    label: 'About',
    children: [
      { href: '/about', label: 'About' },
      { href: '/testimonials', label: 'Testimonials' },
    ],
  },
  {
    label: 'Get Help',
    children: [
      { href: '/mold-assessment', label: 'Mold Symptoms Assessment' },
      { href: '/mold-recovery-programs', label: 'Program' },
      { href: '/aubree-ai', label: 'Aubree AI' },
      { href: '/faq', label: 'FAQ' },
    ],
  },
  {
    label: 'Learn',
    children: [
      { href: '/mold-illness-guides', label: 'Mold Illness Guides' },
      { href: '/blog', label: 'Blog' },
      { href: '/podcast', label: 'Podcast' },
    ],
  },
  { href: '/store', label: 'Store' },
  { href: '/contact', label: 'Contact' },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut } = useSupabaseAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [openDropdowns, setOpenDropdowns] = React.useState<Set<string>>(new Set());

  const toggleDropdown = (label: string) => {
    setOpenDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  // Reset dropdowns when menu closes
  React.useEffect(() => {
    if (!isMenuOpen) {
      setOpenDropdowns(new Set());
    }
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Check if current user is admin
  React.useEffect(() => {
    async function checkAdminStatus() {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('admins')
        .select('user_id')
        .eq('user_id', user.id)
        .single();
      if (!error && data) {
        setIsAdmin(true);
      }

      setIsAdmin(!!data);
    }

    checkAdminStatus();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    router.push('/');
  };

  return (
    <>
      {/* Header */}
      <nav className="bg-[#FAF9F7] fixed w-full top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Hamburger Menu Button (Left) - visible on all sizes */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="flex items-center gap-2 p-2 text-gray-700 hover:text-gray-900 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
              <span className="text-sm font-medium tracking-wide hidden sm:block">MENU</span>
            </button>

            {/* Centered Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <Logo />
            </div>

            {/* Profile Icon (Right) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-full text-gray-700 hover:text-gray-900 hover:bg-gray-100/50 transition-colors">
                  <Avatar className="h-8 w-8">
                    {user ? (
                      <>
                        <AvatarImage src={undefined} alt="Profile" />
                        <AvatarFallback className="bg-brand-teal text-white text-sm">
                          {(() => {
                            const displayName = user.user_metadata?.display_name || user.user_metadata?.full_name;
                            if (displayName && typeof displayName === 'string' && displayName.length > 0) {
                              return displayName[0].toUpperCase();
                            }
                            return user.email?.charAt(0).toUpperCase() || '?';
                          })()}
                        </AvatarFallback>
                      </>
                    ) : (
                      <>
                        <AvatarFallback className="bg-gray-200 text-gray-600">
                          <User className="h-4 w-4" />
                        </AvatarFallback>
                      </>
                    )}
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                {user ? (
                  <>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {user.user_metadata?.full_name || user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => router.push('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      <span>Profile</span>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => router.push('/admin')}>
                        <Shield className="mr-2 h-4 w-4" />
                        <span>Admin Panel</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Sign out</span>
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => router.push('/auth/signin')}>
                      <User className="mr-2 h-4 w-4" />
                      <span>Sign In</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => router.push('/auth/signup')}>
                      <span className="ml-6">Create Account</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

        {/* Desktop secondary nav - visible to crawlers, hidden on mobile */}
        <div className="hidden lg:flex items-center justify-center gap-6 border-t border-gray-100 py-2">
          <Link href="/mold-assessment" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">Mold Assessment</Link>
          <Link href="/mold-recovery-programs" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">Program</Link>
          <Link href="/blog" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">Blog</Link>
          <Link href="/store" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">Store</Link>
          <Link href="/faq" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">FAQ</Link>
          <Link href="/podcast" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">Podcast</Link>
          <Link href="/about" className="text-xs font-medium tracking-wide text-gray-600 hover:text-brand-teal transition-colors uppercase">About</Link>
        </div>
        </div>
      </nav>

      {/* Full-Screen Split Menu Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-50 transition-all duration-500 ease-in-out",
          isMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible pointer-events-none"
        )}
      >
        {/* Mobile: Stacked layout / Desktop: Side-by-side */}
        <div className="h-full w-full flex flex-col md:flex-row">
          {/* Left Panel - Menu Content */}
          <div
            className={cn(
              "relative h-full md:h-full w-full md:w-[55%] lg:w-[50%] bg-white flex flex-col transition-transform duration-500 ease-in-out",
              isMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}
          >
            {/* X Close Button - Top Right */}
            <button
              onClick={() => setIsMenuOpen(false)}
              className="absolute top-4 right-4 z-50 p-3 text-gray-500 hover:text-gray-900 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-7 w-7" />
            </button>

            {/* Centered Content Container */}
            <div className="flex-1 flex flex-col justify-center items-center px-8 md:px-12">
              {/* Logo */}
              <div className="mb-12 scale-125 md:scale-150">
                <Logo />
              </div>

              {/* Navigation Links - Centered */}
              <nav className="text-center">
                <ul className="space-y-4">
                  {navItems.filter(item => !item.hidden).map((item) => (
                    <li key={item.label}>
                      {item.children ? (
                        // Dropdown item
                        <div>
                          <button
                            onClick={() => toggleDropdown(item.label)}
                            className={cn(
                              "flex items-center justify-center gap-2 w-full py-3 text-lg md:text-xl tracking-[0.2em] font-medium transition-colors hover:text-brand-teal",
                              item.children.some(child => pathname === child.href)
                                ? "text-brand-teal"
                                : "text-gray-800"
                            )}
                          >
                            {item.label.toUpperCase()}
                            <ChevronDown
                              className={cn(
                                "h-5 w-5 transition-transform duration-200",
                                openDropdowns.has(item.label) && "rotate-180"
                              )}
                            />
                          </button>
                          <div
                            className={cn(
                              "overflow-hidden transition-all duration-300 ease-in-out",
                              openDropdowns.has(item.label)
                                ? "max-h-96 opacity-100"
                                : "max-h-0 opacity-0"
                            )}
                          >
                            <ul className="py-3 space-y-2">
                              {item.children.map((child) => (
                                <li key={child.href}>
                                  <Link
                                    href={child.href}
                                    onClick={() => setIsMenuOpen(false)}
                                    className={cn(
                                      "block py-2 text-sm md:text-base tracking-[0.15em] font-medium transition-colors hover:text-brand-teal",
                                      pathname === child.href
                                        ? "text-brand-teal"
                                        : "text-gray-500"
                                    )}
                                  >
                                    {child.label.toUpperCase()}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        // Regular link
                        <Link
                          href={item.href!}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            "block py-3 text-lg md:text-xl tracking-[0.2em] font-medium transition-colors hover:text-brand-teal",
                            pathname === item.href
                              ? "text-brand-teal"
                              : "text-gray-800"
                          )}
                        >
                          {item.label.toUpperCase()}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Auth links */}
                {!user && (
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <Link
                      href="/auth/signin"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 text-lg md:text-xl tracking-[0.2em] font-medium text-gray-800 hover:text-brand-teal transition-colors"
                    >
                      SIGN IN
                    </Link>
                    <Link
                      href="/auth/signup"
                      onClick={() => setIsMenuOpen(false)}
                      className="block py-3 text-lg md:text-xl tracking-[0.2em] font-medium text-gray-800 hover:text-brand-teal transition-colors"
                    >
                      CREATE ACCOUNT
                    </Link>
                  </div>
                )}
              </nav>

              {/* Close Menu Text */}
              <div className="mt-12">
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="text-lg font-serif italic text-gray-500 hover:text-gray-900 transition-colors"
                >
                  Close Menu
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Image (Desktop only) */}
          <div
            className={cn(
              "hidden md:block md:h-full md:w-[45%] lg:w-[50%] transition-all duration-500 ease-in-out cursor-pointer",
              isMenuOpen ? "opacity-100" : "opacity-0"
            )}
            onClick={() => setIsMenuOpen(false)}
            style={{
              backgroundImage: "url('/aubree-menu-image.png')",
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
            }}
            aria-label="Close menu"
          />
        </div>
      </div>
    </>
  );
}
